import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { AppLayout } from "../components/AppLayout";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { loadPreset, savePreset, clearPreset, hasPreset } from "../lib/reportPreset";
import { ClipboardList, FileDown, FileSpreadsheet, Loader2, Bookmark, BookmarkCheck, RotateCcw } from "lucide-react";

const DEFAULT_FIELDS = ["code", "data_ora_evento", "nominativo", "citta_partenza", "citta_arrivo", "importo", "fee_nexus", "partner", "status"];

export default function ReportRichiedentiPage() {
  const [richiedenti, setRichiedenti] = useState([]);
  const [fields, setFields] = useState([]);
  const [richiedente, setRichiedente] = useState("");
  const [status, setStatus] = useState("all");
  const [fatturato, setFatturato] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selected, setSelected] = useState(() => loadPreset("richiedenti", DEFAULT_FIELDS));
  const [presetSaved, setPresetSaved] = useState(() => hasPreset("richiedenti"));
  const savePresetNow = () => { savePreset("richiedenti", selected); setPresetSaved(true); toast.success("Preset colonne salvato", { description: "Verrà riproposto alle prossime estrazioni." }); };
  const resetPreset = () => { clearPreset("richiedenti"); setSelected(DEFAULT_FIELDS); setPresetSaved(false); toast.info("Colonne ripristinate ai valori predefiniti"); };
  const [exporting, setExporting] = useState("");

  useEffect(() => {
    api.get("/reports/richiedente/list").then(({ data }) => setRichiedenti(data)).catch(() => {});
    api.get("/reports/partner/fields").then(({ data }) => setFields(data)).catch(() => {});
  }, []);

  const toggleField = (key) => {
    setSelected((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);
  };

  const generate = async (format) => {
    if (!richiedente) { toast.error("Seleziona un richiedente"); return; }
    if (selected.length === 0) { toast.error("Seleziona almeno un campo"); return; }
    setExporting(format);
    try {
      const params = { richiedente, fields: selected.join(","), format, status };
      if (fatturato !== "all") params.fatturato = fatturato;
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      const res = await api.get("/reports/richiedente", { params, responseType: "blob" });
      const type = format === "pdf" ? "application/pdf"
        : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const url = window.URL.createObjectURL(new Blob([res.data], { type }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Report_Richiedente_${richiedente.replace(/\s/g, "_")}.${format === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Report generato");
    } catch (err) {
      toast.error("Errore", { description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally { setExporting(""); }
  };

  return (
    <AppLayout>
      <div className="max-w-4xl mx-auto nx-fade-in pb-10">
        <div className="mb-6">
          <h1 className="text-3xl font-bold font-heading text-slate-900 tracking-tight flex items-center gap-2">
            <ClipboardList className="h-7 w-7 text-primary" /> Report Richiedenti
          </h1>
          <p className="text-sm text-slate-500 mt-1">Contabilizza mensilmente i trasporti per ogni richiedente, con i campi che ti servono.</p>
        </div>

        <Card className="p-6 border-slate-200 mb-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="nx-label">Richiedente</Label>
              <Select value={richiedente} onValueChange={setRichiedente}>
                <SelectTrigger data-testid="report-richiedente" className="mt-1.5"><SelectValue placeholder="Seleziona richiedente" /></SelectTrigger>
                <SelectContent>
                  {richiedenti.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="nx-label">Stato</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid="report-richiedente-status" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti gli stati</SelectItem>
                  <SelectItem value="pubblicata">Pubblicata</SelectItem>
                  <SelectItem value="assegnato">Assegnato</SelectItem>
                  <SelectItem value="completata">Completata</SelectItem>
                  <SelectItem value="annullata">Annullata</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="nx-label">Da (data evento)</Label>
              <Input data-testid="report-richiedente-from" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="mt-1.5" />
            </div>
            <div>
              <Label className="nx-label">A (data evento)</Label>
              <Input data-testid="report-richiedente-to" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="mt-1.5" />
            </div>
            <div>
              <Label className="nx-label">Fatturato</Label>
              <Select value={fatturato} onValueChange={setFatturato}>
                <SelectTrigger data-testid="report-richiedente-fatturato" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  <SelectItem value="no">Fatturare</SelectItem>
                  <SelectItem value="si">Già fatturati</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </Card>

        <Card className="p-6 border-slate-200 mb-5">
          <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
            <div>
              <Label className="nx-label mb-1 block">Campi da includere</Label>
              <p className="text-xs text-slate-500">Spunta le colonne da esportare. Gli importi vengono totalizzati.
                {presetSaved && <span data-testid="report-richiedente-preset-badge" className="ml-2 inline-flex items-center gap-1 text-emerald-700 font-medium"><BookmarkCheck className="h-3 w-3" /> preset attivo</span>}
              </p>
            </div>
            <div className="flex gap-2">
              <Button data-testid="report-richiedente-save-preset" size="sm" variant="outline" className="gap-1.5" onClick={savePresetNow} disabled={selected.length === 0}>
                <Bookmark className="h-3.5 w-3.5" /> Salva preset
              </Button>
              <Button data-testid="report-richiedente-reset-preset" size="sm" variant="ghost" className="gap-1.5 text-slate-500" onClick={resetPreset}>
                <RotateCcw className="h-3.5 w-3.5" /> Default
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {fields.map((f) => (
              <label key={f.key} htmlFor={`rfld-${f.key}`}
                className="flex items-center gap-2.5 rounded-md border border-slate-200 px-3 py-2.5 cursor-pointer hover:bg-slate-50 transition-colors">
                <Checkbox id={`rfld-${f.key}`} data-testid={`report-richiedente-field-${f.key}`}
                  checked={selected.includes(f.key)} onCheckedChange={() => toggleField(f.key)} />
                <span className="text-sm text-slate-700">{f.label}{f.numeric && <span className="text-primary ml-1">Σ</span>}</span>
              </label>
            ))}
          </div>
        </Card>

        <div className="flex justify-end gap-3">
          <Button data-testid="report-richiedente-xlsx" variant="outline" className="gap-2" onClick={() => generate("xlsx")} disabled={!!exporting}>
            {exporting === "xlsx" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />} Excel
          </Button>
          <Button data-testid="report-richiedente-pdf" className="gap-2" onClick={() => generate("pdf")} disabled={!!exporting}>
            {exporting === "pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />} PDF
          </Button>
        </div>
      </div>
    </AppLayout>
  );
}
